import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { User, Location, Status } from '../shared/model/user';
import supabase from '@/shared/api/supabase';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Permission } from '@/shared/model/permission';
import * as Sentry from '@sentry/react';

interface AuthContextType {
  user: User | null;
  signedIn: boolean;
  loading: boolean;
  logout: () => Promise<void>;
  invalid: boolean;
}

interface CustomJwtPayload extends JwtPayload {
  app_metadata: {
    user_info: {
      name: string;
      lastName: string;
      email: string;
      role: string;
      startDate: string;
      location: Location;
      permissions: string[];
      status: Status;
    };
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setLoading(true);
        setSignedIn(!!session);
        if (session) {
          const jwt = jwtDecode<CustomJwtPayload>(session.access_token);
          setInvalid(!jwt.app_metadata.user_info);
          const user: User = {
            ...jwt.app_metadata.user_info,
            id: session.user.id,

            permissions:
              (jwt.app_metadata.user_info.permissions as Permission[]) || [],
          };
          setUser(user);
        }
        setLoading(false);
      },
    );
    return () => authListener.subscription.unsubscribe();
  }, []);

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSignedIn(false);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const value = {
    user,
    signedIn,
    loading,
    logout,
    invalid,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
